import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { SigninModal } from '@/components/Auth/SigninModal';
import { SignUpModal } from '@/components/Auth/SignUpModal';
import { useAuth } from '@/hooks/useAuth';

/**
 * Wave 3.5 — brand-first hero copy.
 * One headline + one supporting sentence + CTA group (no cycling that overpowers brand).
 */
export const HeroText = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [signInOpen, setSignInOpen] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);

  return (
    <Box
      sx={{
        textAlign: { xs: 'center', md: 'left' },
        color: '#f7f9fb',
        maxWidth: 560,
      }}
    >
      <Typography
        component="p"
        className="taste-rise font-display"
        sx={{
          fontFamily: 'var(--font-display)',
          fontSize: { xs: '2.75rem', sm: '3.5rem', md: '4.25rem' },
          fontWeight: 800,
          letterSpacing: '-0.04em',
          lineHeight: 0.95,
          mb: 2,
          color: '#fff',
          textShadow: '0 2px 24px rgba(0,0,0,0.35)',
        }}
      >
        Unbeatable
      </Typography>

      <Typography
        component="h1"
        className="taste-rise-delay-1 font-display"
        sx={{
          fontFamily: 'var(--font-display)',
          fontSize: { xs: '1.35rem', md: '1.65rem' },
          fontWeight: 600,
          letterSpacing: '-0.02em',
          lineHeight: 1.25,
          mb: 1.5,
          color: 'rgba(247,249,251,0.95)',
        }}
      >
        Buy with clear rates — no buyer&apos;s agent required.
      </Typography>

      <Typography
        className="taste-rise-delay-2"
        sx={{
          fontSize: { xs: '1rem', md: '1.125rem' },
          lineHeight: 1.55,
          color: 'rgba(247,249,251,0.78)',
          mb: 3.5,
          maxWidth: 480,
          mx: { xs: 'auto', md: 0 },
        }}
      >
        Accurate loan estimates, cash purchase paths, and offer-to-close tools
        built for investors and homebuyers.
      </Typography>

      <Box
        className="taste-rise-delay-3"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          justifyContent: { xs: 'center', md: 'flex-start' },
        }}
      >
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            if (isAuthenticated) navigate('/properties');
            else setSignUpOpen(true);
          }}
          sx={{
            px: 3.5,
            py: 1.25,
            fontWeight: 700,
            fontFamily: 'var(--font-display)',
            backgroundColor: '#f7f9fb',
            color: 'var(--primary-navy)',
            '&:hover': {
              backgroundColor: '#e6ebf2',
            },
          }}
        >
          {isAuthenticated ? 'Open properties' : 'Get started'}
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => {
            if (isAuthenticated) navigate('/pricing');
            else setSignInOpen(true);
          }}
          sx={{
            px: 3,
            py: 1.25,
            fontWeight: 600,
            fontFamily: 'var(--font-display)',
            borderColor: 'rgba(247,249,251,0.55)',
            color: '#f7f9fb',
            '&:hover': {
              borderColor: '#f7f9fb',
              backgroundColor: 'rgba(247,249,251,0.08)',
            },
          }}
        >
          {isAuthenticated ? 'Investor access' : 'Sign in'}
        </Button>
      </Box>

      <SigninModal isOpen={signInOpen} onClose={() => setSignInOpen(false)} />
      <SignUpModal isOpen={signUpOpen} onClose={() => setSignUpOpen(false)} />
    </Box>
  );
};
